"use client";

import { OfferCard } from "@/composites/landing";
import type { Cta } from "@/composites/landing/types";
import { normalizeImageAsset } from "@/app/utilities/images";
import { useForwardedCta } from "@/app/utilities/query-params/use-forwarded-cta";
import type { ImageAsset } from "@/types/references";

export interface FeaturedOfferBlock {
	blockType: "featured_offer";
	section_title?: string;
	badge_text?: string;
	title: string;
	offer_image?: ImageAsset;
	price_text?: string;
	original_price_text?: string;
	description?: string;
	features?: { label: string }[];
	disclaimer?: { title?: string; body?: string };
	cta?: Cta;
}

/**
 * Adapter for the `featured_offer` block. Maps the typed CMS payload to the
 * `OfferCard` composite — no styling, no fetching. `offer_image` arrives as
 * Contentstack's `image` global field; the shared `normalizeImageAsset`
 * utility (also used by the legacy hero/featured-card blocks) flattens it to
 * `{url, alt}` before handing it to the composite.
 */
export function FeaturedOffer({ block }: { block: FeaturedOfferBlock }) {
	const withForwardedCta = useForwardedCta();

	return (
		<OfferCard
			sectionTitle={block.section_title}
			badgeText={block.badge_text}
			title={block.title}
			priceText={block.price_text}
			originalPriceText={block.original_price_text}
			description={block.description}
			features={block.features?.map((feature) => feature.label)}
			disclaimer={block.disclaimer}
			cta={withForwardedCta(block.cta)}
			image={normalizeImageAsset(block.offer_image, block.title)}
		/>
	);
}
